// Single source of truth for the public website footer.
//
// Two surfaces render this footer from completely different runtimes:
//   1. The live home page (/p/<slug>) — React component
//      client/src/vault-template/components/Contact.tsx.
//   2. The legal pages (/p/<slug>/terms|privacy) — server-rendered HTML string
//      in server/routes.ts.
//
// They can't share JSX (one runs in the browser, one is a plain HTML string on
// the server), so instead they share this pure, runtime-agnostic module. All the
// *decisions* — business-name cleaning, address assembly, phone formatting,
// social-link detection, which icon a URL maps to, and every link target — live
// here. Each renderer is a thin mapper from this model to its own markup. Change
// the footer's content/logic once here and BOTH footers update in lockstep.

import { isInternalWebsiteCtaDestination } from "./websiteCtaDestinations";

export type FooterSocialKey =
  | "facebook"
  | "twitter"
  | "instagram"
  | "linkedin"
  | "youtube"
  | "tiktok"
  | "github"
  | "googleBusiness"
  | "globe";

export interface FooterSocial {
  href: string;
  label: string;
  key: FooterSocialKey;
}

// Inner SVG markup (lucide glyphs) keyed by platform. The server inlines these
// directly; the React client maps the same keys to lucide-react components (see
// client/src/lib/socialLinks.ts) so the icon CHOICE is shared even though the
// glyph representation differs per runtime.
export const FOOTER_SOCIAL_SVG_PATHS: Record<FooterSocialKey, string> = {
  facebook: '<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>',
  twitter:
    '<path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>',
  instagram:
    '<rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>',
  linkedin:
    '<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/>',
  youtube:
    '<path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/><path d="m10 15 5-3-5-3z"/>',
  tiktok: '<circle cx="8" cy="18" r="4"/><path d="M12 18V2l7 4"/>',
  github:
    '<path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.4 5.4 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/><path d="M9 18c-4.51 2-5-2-7-2"/>',
  googleBusiness:
    '<path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/>',
  globe:
    '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
};

const SOCIAL_PATTERNS: { re: RegExp; key: FooterSocialKey; label: string }[] = [
  { re: /(^|\.)facebook\.com$|(^|\.)fb\.com$|(^|\.)fb\.me$/, key: "facebook", label: "Facebook" },
  { re: /(^|\.)twitter\.com$|(^|\.)x\.com$|(^|\.)t\.co$/, key: "twitter", label: "X / Twitter" },
  { re: /(^|\.)instagram\.com$|(^|\.)instagr\.am$/, key: "instagram", label: "Instagram" },
  { re: /(^|\.)linkedin\.com$|(^|\.)lnkd\.in$/, key: "linkedin", label: "LinkedIn" },
  { re: /(^|\.)youtube\.com$|(^|\.)youtu\.be$/, key: "youtube", label: "YouTube" },
  { re: /(^|\.)tiktok\.com$/, key: "tiktok", label: "TikTok" },
  { re: /(^|\.)github\.com$/, key: "github", label: "GitHub" },
  {
    re: /(^|\.)g\.page$|(^|\.)goo\.gl$|(^|\.)maps\.app\.goo\.gl$|(^|\.)google\.com$|(^|\.)business\.google\.com$/,
    key: "googleBusiness",
    label: "Google Business Profile",
  },
];

// Normalize a raw social URL into { href, label, key }. Unknown hosts fall back
// to the "globe" key with the bare hostname as the label.
export function detectFooterSocial(rawUrl: string): FooterSocial | null {
  const trimmed = (rawUrl || "").trim();
  if (!trimmed) return null;
  const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let host: string;
  try {
    host = new URL(withProto).hostname.toLowerCase();
  } catch {
    return null;
  }
  for (const p of SOCIAL_PATTERNS) {
    if (p.re.test(host)) return { href: withProto, label: p.label, key: p.key };
  }
  return { href: withProto, label: host.replace(/^www\./, ""), key: "globe" };
}

// A footer social destination can be saved through the generic links list and
// through the dedicated Google Business Profile setting. Normalize only the
// comparison identity (not the rendered href) so equivalent host casing or a
// trailing slash never creates duplicate footer icons.
function footerSocialIdentity(social: FooterSocial): string {
  try {
    const url = new URL(social.href);
    const pathname =
      url.pathname === "/" ? "" : url.pathname.replace(/\/+$/, "");
    return `${url.protocol.toLowerCase()}//${url.hostname.toLowerCase()}${pathname}${url.search}`;
  } catch {
    return social.href;
  }
}

export interface FooterModelInput {
  businessName?: string | null;
  wideLogoUrl?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
  socialLinks?: (string | null | undefined)[] | null;
  /** Public Google Maps / Business Profile listing. It renders alongside the
   * owner's normal social links, never as a separate review request. */
  googleBusinessProfileUrl?: string | null;
  /** Footer phone-link behavior — the owner's consent for how visitors may
   *  reach them: "call" → tel: only, "text" → sms: only, "both" → both links,
   *  "none" → the number renders as plain text with no link. Anything else
   *  (incl. undefined/legacy) is treated as "call" — the historical default —
   *  so existing owners keep click-to-call. */
  footerPhoneAction?: "call" | "text" | "both" | "none" | string | null;
  /** Optional starter message pre-filled into the visitor's texting app. Only
   *  applied to sms: links ("text"/"both"); appended URL-encoded as a
   *  `?&body=` parameter. Blank/absent → a plain sms: link (current behavior).
   *  Ignored entirely for "call" (tel:) links. */
  footerPhoneText?: string | null;
  /** The page slug. When present, links resolve to /p/<slug>...; when absent
   *  (preview/design routes) home + legal hrefs are null so callers render them
   *  inert. */
  slug?: string | null;
  /** Copyright year; defaults to the current year. */
  year?: number;
  /** Deprecated booking inputs retained for non-footer callers/tests; footer
   * resolution intentionally ignores them. */
  bookingUrl?: string | null;
  bookingTarget?: string | null;
  scheduleAvailable?: boolean | null;
  /** Owner's online-booking link. When present (and the owner hasn't turned
   *  the button off) the footer renders a stand-out booking CTA at the end of
   *  the socials row. */
  /** Owner toggle for the footer booking button — only an explicit false
   *  hides it (default ON once a bookingUrl exists). */
  footerShowBookingButton?: boolean | null;
  /** Custom label for the booking button; blank = "Schedule Here". */
  footerBookingLabel?: string | null;
  footerCtaUrl?: string | null;
  /** Ignored legacy payload field. "Powered by InControl" always links home. */
  referralCode?: string | null;
}

export interface FooterModel {
  /** Business name with trailing dots stripped; "" when unset. */
  businessName: string;
  wideLogoUrl: string;
  /** /p/<slug> when slug present, else null (render inert). */
  homeHref: string | null;
  /** Deep link that opens the home page's standalone Contact modal:
   *  /p/<slug>#contact-us when slug present, else null. The live single-page
   *  React footer opens the modal directly via a button; this absolute form is
   *  for the standalone legal pages, which navigate back to the home page (where
   *  the React footer opens the modal on the #contact-us hash). */
  contactHref: string | null;
  copyright: string;
  /** Display phone (as entered); "" when unset. */
  phone: string;
  /** Primary phone link href including scheme: the call link when calling is
   *  allowed, else the text link, else "" ("none" or no phone). Callers use
   *  this verbatim as the anchor href when rendering the number itself as a
   *  single link — the scheme is decided here so both footers stay in
   *  lockstep. */
  phoneHref: string;
  /** "tel:<digits>" when the owner allows calls ("call"/"both"), else "". */
  callHref: string;
  /** "sms:<digits>[?&body=…]" when the owner allows texts ("text"/"both"),
   *  else "". When BOTH hrefs are set, renderers show small "Call | Text"
   *  links next to the (plain-text) number instead of linking the number. */
  textHref: string;
  addressText: string;
  /** /p/<slug>/terms when slug present, else null. */
  termsHref: string | null;
  /** /p/<slug>/privacy when slug present, else null. */
  privacyHref: string | null;
  socials: FooterSocial[];
  poweredByHref: string;
  /** Booking CTA: the owner's booking link (external kind) or the Sessions
   *  tab anchor (schedule kind: "/p/<slug>?tab=sessions#class-schedule", or
   *  "?tab=sessions#class-schedule" without a slug); "" when unset or turned off —
   *  renderers show the button only when non-empty. Decided HERE so all three
   *  footers (vault, SPA marketing, server-rendered legal) stay in lockstep. */
  bookingHref: string;
  /** What bookingHref points at: "schedule" navigates to the in-page class
   *  schedule section (same-tab, or an in-page scroll on the live React page);
   *  "external" = external booking link (opens in new tab); "none" = no
   *  booking CTA (bookingHref is ""). */
  bookingKind: "schedule" | "external" | "internal" | "none";
  /** Booking button label ("Schedule Here" default for the external link,
   *  "Save Your Spot" default for the schedule target; owner-editable). */
  bookingLabel: string;
}

/** Default label for the footer booking button (blank column = this). */
export const DEFAULT_BOOKING_LABEL = "Learn More";
/** Default label when the booking CTA targets the class schedule. */
export const DEFAULT_SCHEDULE_BOOKING_LABEL = "Learn More";

/** DOM id of the public Sessions-tab schedule detail (ClassSchedule.tsx). */
export const CLASS_SCHEDULE_SECTION_ID = "class-schedule";
/** Public tab query value used by booking links that target live sessions. */
export const SESSIONS_TAB_QUERY = "sessions";

/** Resolved booking CTA destination. Returns {kind:"schedule"} when the class
 *  schedule is available, {kind:"external",href} when an external booking URL
 *  is set (and the target is not "schedule"), or null (hide everywhere).
 *  The explicit-hide toggle (footerShowBookingButton === false) always wins;
 *  undefined/null keeps the default-ON behavior. */
export type BookingCtaResolution =
  | { kind: "schedule" }
  | { kind: "external"; href: string };
export function resolveBookingCta(input: {
  scheduleAvailable?: boolean | null;
  bookingUrl?: string | null;
  bookingTarget?: string | null;
  footerShowBookingButton?: boolean | null;
  footerCtaUrl?: string | null;
}): BookingCtaResolution | null {
  if (input.footerShowBookingButton === false) return null;
  if (input.footerCtaUrl?.trim()) {
    const href = input.footerCtaUrl.trim();
    return href === "booking" ? { kind: "schedule" } : { kind: "external", href };
  }
  if (input.scheduleAvailable === true) return { kind: "schedule" };
  if (input.bookingUrl && input.bookingTarget !== "schedule") {
    return { kind: "external", href: input.bookingUrl.trim() };
  }
  return null;
}

const trim = (v: string | null | undefined): string => (v || "").trim();

/** Normalize a stored footer_phone_action value to the 4-value enum. Unknown/
 *  legacy/blank values fall back to "call" (the historical default). Shared by
 *  every payload builder so client and server agree on the same coercion. */
export type FooterPhoneAction = "call" | "text" | "both" | "none";
export function normalizeFooterPhoneAction(v: string | null | undefined): FooterPhoneAction {
  return v === "text" || v === "both" || v === "none" ? v : "call";
}

// Format a phone number for DISPLAY. US/NANP 10-digit numbers (and 11-digit
// numbers with a leading country code 1) are formatted with dashes
// (e.g. "6105743199" → "610-574-3199", "16105743199" → "1-610-574-3199").
// Anything else (international, extensions, partial) is returned as entered so
// we never mangle a number we don't recognize. Both public footers render this.
export function formatDisplayPhone(raw: string | null | undefined): string {
  const entered = trim(raw);
  if (!entered) return "";
  const digits = entered.replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    const d = digits.slice(1);
    return `1-${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return entered;
}

// Build the normalized footer view-model. This is the ONE place the footer's
// content/logic is decided; both the React footer and the server-rendered legal
// footer consume the result.
export function buildFooterModel(input: FooterModelInput): FooterModel {
  const slug = trim(input.slug);
  const slugPath = slug ? `/p/${encodeURIComponent(slug)}` : null;

  const cityRegionPostal = [
    trim(input.city),
    [trim(input.region), trim(input.postalCode)].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const addressText = [
    trim(input.addressLine1),
    trim(input.addressLine2),
    cityRegionPostal,
    trim(input.country),
  ]
    .filter(Boolean)
    .join(", ");

  const phone = trim(input.phone);
  const phoneDigits = phone.replace(/[^\d+]/g, "");
  const action = input.footerPhoneAction;
  // Owner consent: calling is allowed unless they explicitly chose text-only
  // or none (legacy/unknown values keep the historical click-to-call default).
  const allowCall = action !== "text" && action !== "none";
  const allowText = action === "text" || action === "both";
  // Pre-filled starter message is only meaningful for sms: links. Appended as
  // `?&body=` (the cross-platform iOS/Android form) with the message URL-encoded.
  const phoneBody = allowText ? trim(input.footerPhoneText) : "";
  const callHref = allowCall && phoneDigits ? `tel:${phoneDigits}` : "";
  const textHref =
    allowText && phoneDigits
      ? `sms:${phoneDigits}${phoneBody ? `?&body=${encodeURIComponent(phoneBody)}` : ""}`
      : "";
  const googleBusinessProfileUrl = trim(input.googleBusinessProfileUrl);
  const googleBusinessProfile = googleBusinessProfileUrl
    ? detectFooterSocial(googleBusinessProfileUrl)
    : null;
  const socialUrls = [...(input.socialLinks || [])];
  // The listing is a normal social destination. Append it only when the owner
  // has not already entered that exact link in the generic list.
  if (
    googleBusinessProfile &&
    !socialUrls.some(
      (url) => {
        const social = detectFooterSocial(url || "");
        return social
          ? footerSocialIdentity(social) === footerSocialIdentity(googleBusinessProfile)
          : false;
      },
    )
  ) {
    socialUrls.push(googleBusinessProfileUrl);
  }
  const socials = socialUrls
    .map((u) => detectFooterSocial(u || ""))
    .filter((s): s is FooterSocial => !!s);

  // Footer CTA: the toggle is the only visibility gate. Older rows that predate
  // footerCtaUrl use the safe general Receptionist destination rather than
  // inheriting any booking/session behavior.
  const destination = trim(input.footerCtaUrl) || "receptionist";
  const bookingResolved = input.footerShowBookingButton === false
    ? null
    : {
        kind: isInternalWebsiteCtaDestination(destination) ? "internal" as const : "external" as const,
        href: destination,
      };
  const bookingKind: FooterModel["bookingKind"] =
    bookingResolved?.kind === "internal"
      ? "internal"
      : bookingResolved?.kind === "external"
        ? "external"
        : "none";
  const bookingHref =
    bookingResolved?.kind === "internal"
      ? destination === "page"
        ? slugPath || "/"
        : destination === "blog"
        ? `${slugPath ?? ""}/blog`
        : /^page:[a-z0-9]+(?:-[a-z0-9]+)*$/.test(destination)
          ? `${slugPath ?? ""}/${destination.slice(5)}`
        : destination === "booking"
        ? `${slugPath ?? ""}?tab=${SESSIONS_TAB_QUERY}#${CLASS_SCHEDULE_SECTION_ID}`
        : destination.startsWith("#")
          ? `${slugPath ?? ""}${destination}`
          : `${slugPath ?? ""}#${destination.replace(/:/g, "-")}`
      : bookingResolved?.kind === "external"
        ? bookingResolved.href
        : "";

  return {
    businessName: trim(input.businessName).replace(/\.+$/, "").trim(),
    wideLogoUrl: trim(input.wideLogoUrl),
    homeHref: slugPath,
    contactHref: slugPath ? `${slugPath}#contact-us` : null,
    copyright: `Copyright ${input.year ?? new Date().getFullYear()} All Rights Reserved`,
    phone: formatDisplayPhone(phone),
    phoneHref: callHref || textHref,
    callHref,
    textHref,
    addressText,
    termsHref: slugPath ? `${slugPath}/terms` : null,
    privacyHref: slugPath ? `${slugPath}/privacy` : null,
    socials,
    poweredByHref: "https://useincontrol.com",
    bookingHref,
    bookingKind,
    bookingLabel: trim(input.footerBookingLabel) || DEFAULT_BOOKING_LABEL,
  };
}
